<template>
  <div class="audios-page">
    <div class="content-wrapper">
      <aside class="sidebar">
        <nav class="nav-card">
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <div class="brand-copy">
              <h1 class="brand-name">树苗英语</h1>
              <span class="brand-subtitle">TREE SAPLING</span>
            </div>
          </div>

          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>

          <div class="nav-menu-visual" aria-hidden="true">
            <img :src="menuBgImage" alt="" class="nav-menu-image" />
          </div>
        </nav>
      </aside>

      <main class="audios-shell" :class="{ 'has-player': currentAudioTitle }">
        <div class="mobile-topbar" :class="{ 'is-scrolled': isTopbarScrolled }">
          <button type="button" class="mobile-back-btn" @click="handleGoBack" aria-label="返回上一页">
            <font-awesome-icon icon="chevron-left" />
          </button>
          <h1 class="mobile-topbar-title">{{ mobilePageTitle }}</h1>
          <span class="mobile-topbar-spacer" aria-hidden="true"></span>
        </div>
        <div class="mobile-topbar-offset" aria-hidden="true"></div>

        <header class="page-header">
          <button type="button" class="back-btn" @click="handleGoBack" aria-label="返回上一页">
            <font-awesome-icon icon="chevron-left" />
          </button>
        </header>

        <nav class="audio-tabs" aria-label="音频分类">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="audio-tab"
            :class="{ active: activeTab === tab.id }"
            @click="selectTab(tab.id)"
          >
            {{ tab.label }}
          </button>
        </nav>

        <section
          ref="audioListRef"
          class="audio-list"
          :class="{
            'all-list-frame': isSeriesListTab,
            'saved-list-frame': !isSeriesListTab
          }"
          aria-label="音频系列列表"
        >
          <template v-if="showSeriesSkeleton">
            <div
              v-for="index in queryParams.pageSize"
              :key="`audio-series-skeleton-${index}`"
              class="audio-item audio-skeleton-item"
            >
              <el-skeleton animated>
                <template #template>
                  <div v-if="isSeriesListTab" class="audio-skeleton-row">
                    <el-skeleton-item variant="image" class="audio-skeleton-cover" />
                    <div class="audio-skeleton-copy">
                      <el-skeleton-item variant="text" class="audio-skeleton-title" />
                      <el-skeleton-item variant="text" class="audio-skeleton-subtitle" />
                      <div class="audio-skeleton-meta">
                        <el-skeleton-item variant="button" class="audio-skeleton-badge" />
                        <el-skeleton-item variant="text" class="audio-skeleton-kind" />
                      </div>
                    </div>
                  </div>
                  <div v-else class="audio-skeleton-row saved-audio-skeleton-row">
                    <el-skeleton-item variant="circle" class="saved-audio-skeleton-icon" />
                    <div class="saved-audio-skeleton-copy">
                      <el-skeleton-item variant="text" class="saved-audio-skeleton-title" />
                      <el-skeleton-item variant="text" class="saved-audio-skeleton-date" />
                    </div>
                    <el-skeleton-item variant="button" class="saved-audio-skeleton-button" />
                  </div>
                </template>
              </el-skeleton>
            </div>
          </template>

          <article
            v-show="!showSeriesSkeleton"
            v-for="item in visibleSeriesItems"
            :key="item.id"
            class="audio-item"
            :class="{ playing: currentAudioItemId === item.id }"
            @click="handleAudioItemClick(item)"
          >
            <template v-if="isSeriesListTab">
              <div class="audio-cover-frame">
                <img
                  :src="item.coverUrl"
                  :alt="item.title"
                  class="audio-cover"
                  @error="handleAudioCoverError"
                />
              </div>

              <div class="audio-item-copy">
                <h2 class="audio-title">{{ item.title }}</h2>
                <p class="audio-subtitle">{{ item.subtitle }}</p>
                <div class="audio-meta">
                  <span
                    v-for="ageRange in item.ageRanges"
                    :key="`${item.id}-${ageRange}`"
                    class="audio-age-badge"
                  >
                    {{ ageRange }}
                  </span>
                  <span class="audio-kind">
                    <font-awesome-icon icon="headphones" />
                    音频
                  </span>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="saved-audio-icon">
                <font-awesome-icon :icon="currentAudioItemId === item.id ? 'volume-high' : 'headphones'" />
              </div>
              <div class="saved-audio-copy">
                <h2 class="saved-audio-title">{{ item.title }}</h2>
                <p class="saved-audio-date">{{ item.date }}</p>
              </div>
              <button
                type="button"
                class="saved-favorite-btn"
                :class="{ active: item.isFavorite }"
                :disabled="item.isFavoriteLoading"
                :aria-label="item.isFavorite ? '取消收藏' : '收藏'"
                @click.stop="toggleFavorite(item)"
              >
                <font-awesome-icon :icon="item.isFavorite ? 'heart-circle-check' : 'heart'" />
                <span>{{ item.isFavorite ? '已收藏' : '收藏' }}</span>
              </button>
            </template>
          </article>

          <div v-if="isSeriesPageLoading" class="audio-page-loading">
            <el-skeleton animated>
              <template #template>
                <el-skeleton-item variant="text" class="audio-page-loading-line" />
              </template>
            </el-skeleton>
          </div>

          <div v-if="!showSeriesSkeleton && visibleSeriesItems.length === 0" class="empty-state">
            <font-awesome-icon icon="headphones" />
            <p>没有音频内容</p>
          </div>
        </section>

        <Transition name="player-dock">
          <section v-if="currentAudioTitle" class="audios-player-dock" aria-label="音频播放器">
            <div class="audios-player-copy">
              <span class="audios-player-label">正在播放</span>
              <strong class="audios-player-title">{{ currentAudioTitle }}</strong>
            </div>
            <audio
              ref="audioPlayerRef"
              class="audios-player"
              :src="currentAudioSrc"
              controls
              preload="metadata"
              @ended="handleAudioEnded"
            ></audio>
          </section>
        </Transition>
      </main>
    </div>

    <ParentPasswordModal ref="parentPasswordModalRef" />
    <LoginPromptModal v-model="isLoginPromptOpen" @confirm="goLogin" />
  </div>
</template>

<script setup lang="ts">
import logo2Image from '../assets/logo2.png'
import menuBgImage from '../assets/menu_bg.jpg'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getAudioSeriesPage, type AudioSeries } from '@/api/course-audio'
import { addFavorites, deleteFavorites, getFavoritesPage, type FavoritesVO } from '@/api/favorites'
import { addHistory, getHistoryPage, type HistoryVO } from '@/api/history'
import { getAccessToken } from '@/config/axios/auth'
import LoginPromptModal from '@/components/LoginPromptModal.vue'

const route = useRoute()
const router = useRouter()
const routeStage = Array.isArray(route.query.stage) ? route.query.stage[0] : route.query.stage
const routeStageText = String(routeStage || '1')
const hasRouteStage = computed(() => routeStage != null && String(routeStage).trim() !== '')
const AUDIO_CONTENT_TYPE = 2
const isGuestUser = computed(() => getAccessToken() == null)

const queryParams = ref({
  pageNo: 1,
  pageSize: 10,
  stage: routeStage
})

const audioSeriesList = ref<AudioSeries[]>([])
const favoriteAudioList = ref<FavoritesVO[]>([])
const historyAudioList = ref<HistoryVO[]>([])
const dataTotal = ref(0)
const isLoading = ref(false)
const activeNav = ref('')
const activeTab = ref('all')
const isLoginPromptOpen = ref(false)
const isTopbarScrolled = ref(false)
const favoriteLoadingIds = ref<number[]>([])
const favoriteStateOverrides = ref<Record<number, boolean>>({})
const currentAudioItemId = ref<number | string>('')
const currentAudioTitle = ref('')
const currentAudioSrc = ref('')
const audioPlayerRef = ref<HTMLAudioElement | null>(null)
const parentPasswordModalRef = ref(null)
const audioListRef = ref<HTMLElement | null>(null)
let audioScrollContainer: HTMLElement | null = null

const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看历史', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]

const stageAgeRangeMap: Record<string, string> = {
  sense: '0-3岁',
  sprout: '3-6岁',
  expression: '6-9岁',
  ability: '9-12岁',
  1: '0-3岁',
  2: '3-6岁',
  3: '6-9岁',
  4: '9-12岁'
}

const mobilePageTitle = computed(() => {
  const title = typeof route.query.title === 'string' ? route.query.title.trim() : ''
  return title || '音频库'
})
const isAudioBookEntry = computed(() => mobilePageTitle.value.includes('有声书'))

const routeStageValues = computed(() => routeStageText.split(',').map((stage) => stage.trim()).filter(Boolean))
const isRouteStagePair = (stageValues: string[]) => {
  if (!hasRouteStage.value || routeStageValues.value.length !== stageValues.length) {
    return false
  }

  return stageValues.every((stage) => routeStageValues.value.includes(stage))
}
const isSongStageRoute = computed(() => isRouteStagePair(['1', '2']))
const isBookStageRoute = computed(() => isRouteStagePair(['3', '4']))

const tabs = computed(() => {
  if (isSongStageRoute.value) {
    return [
      { id: 'all', label: '全部' },
      { id: 'stage-1', label: '0-3' },
      { id: 'stage-2', label: '3-6' }
    ]
  }

  if (isBookStageRoute.value) {
    return [
      { id: 'all', label: '全部' },
      { id: 'stage-3', label: '6-9' },
      { id: 'stage-4', label: '9-12' }
    ]
  }

  if (!hasRouteStage.value) {
    if (isAudioBookEntry.value) {
      return [
        { id: 'all', label: '全部' },
        { id: 'stage-3', label: '6-9' },
        { id: 'stage-4', label: '9-12' }
      ]
    }

    return [
      { id: 'all', label: '全部' },
      { id: 'stage-1', label: '0-3' },
      { id: 'stage-2', label: '3-6' }
    ]
  }

  return [
    { id: 'all', label: '全部' },
    { id: 'favorites', label: '收藏' },
    { id: 'recent', label: '最近' }
  ]
})

const dataTotalPages = computed(() => Math.ceil(dataTotal.value / queryParams.value.pageSize))
const hasNextSeriesPage = computed(() => queryParams.value.pageNo < dataTotalPages.value)
const currentListLength = computed(() => {
  if (activeTab.value === 'favorites') {
    return favoriteAudioList.value.length
  }

  if (activeTab.value === 'recent') {
    return historyAudioList.value.length
  }

  return audioSeriesList.value.length
})
const showSeriesSkeleton = computed(() => isLoading.value && currentListLength.value === 0)
const isSeriesPageLoading = computed(() => isLoading.value && currentListLength.value > 0)
const isSeriesListTab = computed(() => !['favorites', 'recent'].includes(activeTab.value))

const visibleSeriesItems = computed(() => {
  if (activeTab.value === 'favorites') {
    return favoriteAudioList.value.map((item) => buildAudioListItem(item, 'favorite'))
  }

  if (activeTab.value === 'recent') {
    return historyAudioList.value.map((item) => buildAudioListItem(item, 'history'))
  }

  return audioSeriesList.value.map((item) => ({
    id: item.id,
    seriesId: item.id,
    title: item.chineseName || '音频课程',
    subtitle: item.englishName || item.description || 'Tree Sapling Audio',
    coverUrl: item.coverUrl || logo2Image,
    ageRanges: routeStageAgeRanges.value,
  }))
})

const buildAudioListItem = (item: FavoritesVO | HistoryVO, source: string) => ({
  id: item.contentId,
  source,
  seriesId: item.seriesId,
  title: item.chinese || item.name || '音频课程',
  subtitle: item.english || item.category || item.name || 'Tree Sapling Audio',
  coverUrl: item.coverUrl || logo2Image,
  audioUrl: item.audioUrl,
  date: item.createTime,
  isFavorite: getAudioFavoriteState(item, source),
  isFavoriteLoading: favoriteLoadingIds.value.includes(item.contentId),
  ageRanges: routeStageAgeRanges.value,
})

const getAudioFavoriteState = (item: FavoritesVO | HistoryVO, source: string) => {
  if (item.contentId in favoriteStateOverrides.value) {
    return favoriteStateOverrides.value[item.contentId]
  }

  return source === 'favorite' || item.isFavorite === 0
}

const setAudioFavoriteState = (audioId: number, isFavorite: boolean) => {
  favoriteStateOverrides.value = {
    ...favoriteStateOverrides.value,
    [audioId]: isFavorite
  }
}

const normalizeAgeRange = (value: string) => {
  const normalizedValue = value.trim()

  if (!normalizedValue) {
    return ''
  }

  return stageAgeRangeMap[normalizedValue] ||
    (normalizedValue.endsWith('岁') ? normalizedValue : `${normalizedValue}岁`)
}

const routeStageAgeRanges = computed(() => {
  if (activeTab.value === 'stage-1') {
    return ['0-3岁']
  }

  if (activeTab.value === 'stage-2') {
    return ['3-6岁']
  }

  if (activeTab.value === 'stage-3') {
    return ['6-9岁']
  }

  if (activeTab.value === 'stage-4') {
    return ['9-12岁']
  }

  if (!hasRouteStage.value) {
    if (activeTab.value === 'stage-1') {
      return ['0-3岁']
    }

    if (activeTab.value === 'stage-2') {
      return ['3-6岁']
    }

    if (activeTab.value === 'stage-3') {
      return ['6-9岁']
    }

    if (activeTab.value === 'stage-4') {
      return ['9-12岁']
    }

    if (isAudioBookEntry.value) {
      return ['6-9岁', '9-12岁']
    }

    return ['0-3岁', '3-6岁']
  }

  const ageRanges = routeStageText
    .split(',')
    .map(normalizeAgeRange)
    .filter(Boolean)

  return ageRanges.length ? ageRanges : ['0-3岁']
})

const handleAudioCoverError = (event: Event) => {
  const image = event.target as HTMLImageElement | null
  if (image && image.src !== logo2Image) {
    image.src = logo2Image
  }
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/category')
}

const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const openLoginPrompt = () => {
  isLoginPromptOpen.value = true
}

const goLogin = () => {
  isLoginPromptOpen.value = false
  router.push('/login')
}

const handleNavItemClick = (item) => {
  if (item.id === 'home') {
    router.push('/')
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
  }
}

const handleSeriesClick = (item) => {
  router.push({
    path: '/audio',
    query: {
      seriesId: item.seriesId || item.id,
      title: mobilePageTitle.value
    }
  })
}

const handleAudioItemClick = (item) => {
  if (isSeriesListTab.value) {
    handleSeriesClick(item)
    return
  }

  playAudioItem(item)
}

const playAudioItem = (item) => {
  if (!item.audioUrl) {
    return
  }

  currentAudioItemId.value = item.id
  currentAudioTitle.value = item.title
  currentAudioSrc.value = item.audioUrl

  if (!isGuestUser.value) {
    addHistory({ contentType: AUDIO_CONTENT_TYPE, contentId: item.id })
  }

  requestAnimationFrame(() => {
    if (!audioPlayerRef.value || !currentAudioSrc.value) {
      return
    }

    audioPlayerRef.value.currentTime = 0
    audioPlayerRef.value.play().catch(() => {})
  })
}

const handleAudioEnded = () => {
  currentAudioItemId.value = ''
  currentAudioTitle.value = ''
  currentAudioSrc.value = ''
}

const getStageForTab = (tabId: string) => {
  if (tabId === 'stage-1') {
    return '1'
  }

  if (tabId === 'stage-2') {
    return '2'
  }

  if (tabId === 'stage-3') {
    return '3'
  }

  if (tabId === 'stage-4') {
    return '4'
  }

  if (hasRouteStage.value) {
    return routeStage
  }

  return undefined
}

const selectTab = (tabId: string) => {
  if (activeTab.value === tabId) {
    return
  }

  if (['favorites', 'recent'].includes(tabId) && isGuestUser.value) {
    openLoginPrompt()
    return
  }

  activeTab.value = tabId
  queryParams.value.pageNo = 1
  queryParams.value.stage = getStageForTab(tabId)
  dataTotal.value = 0
  loadCurrentTabList()
}

const toggleFavorite = async (item) => {
  if (isGuestUser.value) {
    openLoginPrompt()
    return
  }

  const audioId = item.id
  if (!audioId || favoriteLoadingIds.value.includes(audioId)) {
    return
  }

  const wasFavorite = item.isFavorite === true
  const removedFavoriteItems = activeTab.value === 'favorites'
    ? favoriteAudioList.value.filter((favoriteItem) => favoriteItem.contentId === audioId)
    : []

  favoriteLoadingIds.value = [...favoriteLoadingIds.value, audioId]
  setAudioFavoriteState(audioId, !wasFavorite)

  if (wasFavorite && activeTab.value === 'favorites') {
    favoriteAudioList.value = favoriteAudioList.value.filter((favoriteItem) => favoriteItem.contentId !== audioId)
    dataTotal.value = Math.max(0, dataTotal.value - removedFavoriteItems.length)
  }

  try {
    if (wasFavorite) {
      await deleteFavorites(AUDIO_CONTENT_TYPE, audioId)
    } else {
      await addFavorites({
        contentType: AUDIO_CONTENT_TYPE,
        contentId: audioId
      })
    }
  } catch {
    setAudioFavoriteState(audioId, wasFavorite)
    if (removedFavoriteItems.length) {
      favoriteAudioList.value = [...removedFavoriteItems, ...favoriteAudioList.value]
      dataTotal.value += removedFavoriteItems.length
    }
  } finally {
    favoriteLoadingIds.value = favoriteLoadingIds.value.filter((id) => id !== audioId)
  }
}

const handlePageScroll = () => {
  isTopbarScrolled.value = window.scrollY > 2
  if (isPageNearBottom()) {
    loadNextSeriesPage()
  }
}

const isPageNearBottom = () => {
  const scrollContainer = audioListRef.value

  if (scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight + 1) {
    return scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 120
  }

  const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  const pageHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)

  return scrollTop + viewportHeight >= pageHeight - 120
}

const loadNextSeriesPage = async () => {
  if (isLoading.value || !hasNextSeriesPage.value) {
    return
  }

  queryParams.value.pageNo += 1
  await loadCurrentTabList(true)
}

const loadCurrentTabList = async (append = false) => {
  if (activeTab.value === 'favorites') {
    await getFavoriteAudioList(append)
    return
  }

  if (activeTab.value === 'recent') {
    await getHistoryAudioList(append)
    return
  }

  await getAudioSeriesList(append)
}

const getAudioSeriesList = async (append = false) => {
  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    const params = { ...queryParams.value }
    if (params.stage == null || params.stage === '') {
      delete params.stage
    }

    const data = await getAudioSeriesPage(params)
    const list = data?.list || []
    audioSeriesList.value = append ? [...audioSeriesList.value, ...list] : list
    dataTotal.value = data?.total || 0
  } catch {
    if (append) {
      queryParams.value.pageNo = Math.max(1, queryParams.value.pageNo - 1)
    }
  } finally {
    isLoading.value = false
  }
}

const getFavoriteAudioList = async (append = false) => {
  if (isGuestUser.value) {
    openLoginPrompt()
    return
  }

  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    const data = await getFavoritesPage({
      ...queryParams.value,
      contentType: AUDIO_CONTENT_TYPE
    })
    const list = data?.list || []
    favoriteAudioList.value = append ? [...favoriteAudioList.value, ...list] : list
    dataTotal.value = data?.total || 0
  } catch {
    if (append) {
      queryParams.value.pageNo = Math.max(1, queryParams.value.pageNo - 1)
    }
  } finally {
    isLoading.value = false
  }
}

const getHistoryAudioList = async (append = false) => {
  if (isGuestUser.value) {
    openLoginPrompt()
    return
  }

  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    const data = await getHistoryPage({
      ...queryParams.value,
      contentType: AUDIO_CONTENT_TYPE
    })
    const list = data?.list || []
    historyAudioList.value = append ? [...historyAudioList.value, ...list] : list
    dataTotal.value = data?.total || 0
  } catch {
    if (append) {
      queryParams.value.pageNo = Math.max(1, queryParams.value.pageNo - 1)
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  loadCurrentTabList()
  audioScrollContainer = audioListRef.value
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  audioScrollContainer?.addEventListener('scroll', handlePageScroll, { passive: true })
  handlePageScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handlePageScroll)
  audioScrollContainer?.removeEventListener('scroll', handlePageScroll)
  audioScrollContainer = null
})
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.audios-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: #f8f4ee;
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.content-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 100vh;
  height: 100vh;
  overflow: hidden;
}

.sidebar {
  width: 264px;
  flex-shrink: 0;
  height: 100vh;
  overflow: hidden;
  background: #fff9f5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
  position: relative;
  z-index: 2;
}

.nav-card {
  height: 100%;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}

.nav-logo .logo-icon {
  width: 62px;
  height: 62px;
  background: #fff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.brand-name {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #2d3436;
  letter-spacing: 1px;
  line-height: 1.05;
}

.brand-subtitle {
  color: #a78b76;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.2px;
  line-height: 1;
}

.nav-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: clamp(12px, 2vh, 20px);
  padding: clamp(12px, 2vh, 16px) 12px;
  margin: 0;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: clamp(12px, 1.8vh, 18px) 18px;
  border-radius: 14px;
  color: #666;
  font-size: 15px;
  font-weight: 500;
  transition: background 0.2s ease, color 0.2s ease;
  cursor: pointer;
  text-decoration: none;
}

.nav-item:hover {
  background: rgba(255, 138, 61, 0.08);
  color: #ff8a3d;
}

.nav-item.active {
  background: linear-gradient(135deg, #ffe4d4 0%, #ffd4c4 100%);
  color: #ff6b35;
  font-weight: 700;
}

.nav-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.nav-menu-visual {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  margin-top: auto;
  overflow: hidden;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.nav-menu-image {
  display: block;
  flex-shrink: 0;
  width: 100%;
  min-width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center bottom;
}

.audios-shell {
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
  width: min(100%, 980px);
  margin: 0 auto;
  height: 100vh;
  padding: 24px 20px 40px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.audios-shell.has-player {
  padding-bottom: 132px;
}

.page-header {
  display: none;
}

.mobile-topbar,
.mobile-topbar-offset,
.mobile-back-btn,
.mobile-topbar-spacer {
  display: none;
}

.back-btn {
  display: none;
}

.audio-tabs {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin: 0 0 24px;
  flex-wrap: wrap;
}

.audio-tab {
  min-width: 104px;
  min-height: 46px;
  padding: 0 22px;
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  color: #7b6a5f;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(70, 59, 47, 0.05);
  transition: transform 0.22s ease, background 0.22s ease, color 0.22s ease, box-shadow 0.22s ease;
}

.audio-tab:hover {
  transform: translateY(-1px);
}

.audio-tab.active {
  background: linear-gradient(135deg, #ff9d56 0%, #ff7a33 100%);
  color: #fff;
  box-shadow: 0 14px 30px rgba(255, 125, 48, 0.22);
}

.audio-list {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 6px;
  scrollbar-gutter: stable;
}

.audio-list.all-list-frame {
  padding: 18px 16px;
  border-radius: 38px;
  background: rgba(255, 255, 255, 0.68);
  border: 1px dashed rgba(215, 195, 174, 0.58);
}

.audio-list.saved-list-frame .audio-item {
  border-radius: 18px;
}

.audio-list.saved-list-frame {
  border-radius: 22px;
}

.audio-item {
  display: flex;
  align-items: stretch;
  gap: 24px;
  min-height: 204px;
  padding: 22px 26px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  border: none;
  box-shadow: 0 8px 24px rgba(75, 63, 48, 0.04);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.audio-item:hover {
  background: #fff;
  transform: translateY(-1px);
}

.audio-cover-frame {
  width: 252px;
  aspect-ratio: 16 / 9;
  min-height: 0;
  flex: 0 0 252px;
  align-self: center;
  border-radius: 8px;
  overflow: hidden;
  border: none;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  isolation: isolate;
}

.audio-cover {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
  object-position: center;
}

.audio-item-copy {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.audio-title {
  margin: 0;
  color: #1f2328;
  font-size: 28px;
  font-weight: 500;
  line-height: 1.22;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.audio-subtitle {
  margin: 16px 0 0;
  color: #8d8d8d;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.audio-meta {
  display: flex;
  align-items: center;
  gap: 28px;
  min-height: 44px;
  margin-top: 28px;
  flex-wrap: wrap;
}

.audio-age-badge {
  min-width: 76px;
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  background: linear-gradient(135deg, #ffac59 0%, #ff8438 100%);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 500;
  line-height: 1;
}

.audio-kind {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: #777;
  font-size: 20px;
  font-weight: 500;
  line-height: 1;
}

.audio-kind svg {
  color: #b9b9b9;
  font-size: 21px;
}

.audio-list.saved-list-frame {
  gap: 16px;
}

.audio-list.saved-list-frame .audio-item {
  align-items: center;
  gap: 18px;
  min-height: 0;
  padding: 18px 20px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow:
    0 16px 34px rgba(95, 74, 52, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.saved-audio-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  flex: 0 0 auto;
  background: #fdf0e3;
  color: #ff8a3d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.86);
}

.saved-audio-copy {
  min-width: 0;
  flex: 1;
}

.saved-audio-title {
  margin: 0;
  color: #2d3436;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.saved-audio-date {
  margin: 8px 0 0;
  color: #9b8878;
  font-size: 13px;
  font-weight: 700;
}

.saved-favorite-btn {
  flex: 0 0 auto;
  min-width: 92px;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid rgba(255, 173, 111, 0.4);
  border-radius: 999px;
  background: rgba(255, 247, 239, 0.96);
  color: #d9782f;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.saved-favorite-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(217, 120, 47, 0.14);
}

.saved-favorite-btn:disabled {
  cursor: wait;
  opacity: 0.72;
}

.saved-favorite-btn.active {
  border-color: rgba(255, 143, 66, 0.42);
  background: linear-gradient(135deg, #ffac59 0%, #ff8438 100%);
  color: #fff;
  box-shadow: 0 12px 24px rgba(255, 132, 56, 0.2);
}

.audio-list.saved-list-frame .audio-item.playing {
  background: rgba(255, 247, 239, 0.96);
  box-shadow:
    0 18px 40px rgba(255, 140, 66, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
}

.audio-item.playing .saved-audio-icon {
  background: linear-gradient(135deg, #ffac59 0%, #ff8438 100%);
  color: #fff;
}

.audio-item.playing .saved-audio-title {
  color: #ff6508;
}

.audios-player-dock {
  position: fixed;
  left: calc(264px + (100vw - 264px) / 2);
  bottom: 18px;
  z-index: 80;
  width: min(calc(100vw - 304px), 680px);
  padding: 14px 16px;
  border-radius: 20px;
  background: rgba(255, 250, 245, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 40px rgba(95, 74, 52, 0.16);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  transform: translateX(-50%);
}

.audios-player-copy {
  min-width: 0;
  margin-bottom: 10px;
}

.audios-player-label {
  display: block;
  color: #d27a33;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
}

.audios-player-title {
  display: block;
  margin-top: 6px;
  color: #2d3436;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audios-player {
  display: block;
  width: 100%;
  height: 40px;
}

.player-dock-enter-active,
.player-dock-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.player-dock-enter-from,
.player-dock-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}

.audio-skeleton-item {
  cursor: default;
  pointer-events: none;
}

.audio-skeleton-item :deep(.el-skeleton) {
  width: 100%;
}

.audio-skeleton-row {
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
}

.audio-skeleton-cover {
  width: 252px;
  aspect-ratio: 16 / 9;
  height: auto;
  border-radius: 8px;
  flex: 0 0 auto;
}

.audio-skeleton-copy {
  flex: 1;
  min-width: 0;
}

.audio-skeleton-title {
  width: 58%;
  height: 28px;
  border-radius: 999px;
}

.audio-skeleton-subtitle {
  width: 76%;
  height: 24px;
  margin-top: 18px;
  border-radius: 999px;
}

.audio-skeleton-meta {
  display: flex;
  align-items: center;
  gap: 22px;
  margin-top: 28px;
}

.audio-skeleton-badge {
  width: 76px;
  height: 44px;
  border-radius: 12px;
}

.audio-skeleton-kind {
  width: 96px;
  height: 22px;
  border-radius: 999px;
}

.saved-audio-skeleton-row {
  align-items: center;
  gap: 18px;
}

.saved-audio-skeleton-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  flex: 0 0 auto;
}

.saved-audio-skeleton-copy {
  flex: 1;
  min-width: 0;
}

.saved-audio-skeleton-title {
  width: min(420px, 68%);
  height: 18px;
  border-radius: 999px;
}

.saved-audio-skeleton-date {
  width: 148px;
  height: 13px;
  margin-top: 10px;
  border-radius: 999px;
}

.saved-audio-skeleton-button {
  width: 92px;
  height: 40px;
  border-radius: 999px;
  flex: 0 0 auto;
}

.audio-page-loading {
  width: min(240px, 70%);
  align-self: center;
  padding: 4px 0 2px;
}

.audio-page-loading-line {
  height: 14px;
  border-radius: 999px;
}

.empty-state {
  padding: 48px 20px;
  text-align: center;
  color: #9b8878;
  background: rgba(255, 255, 255, 0.76);
  border: 1px dashed rgba(216, 173, 134, 0.5);
}

.empty-state svg {
  font-size: 30px;
  color: #ff8a3d;
}

.empty-state p {
  margin: 14px 0 0;
  font-size: 14px;
  font-weight: 700;
}

@media (max-width: 1200px) {
  .sidebar {
    width: 224px;
  }

  .audios-player-dock {
    left: calc(224px + (100vw - 224px) / 2);
    width: min(calc(100vw - 260px), 680px);
  }
}

@media (max-width: 992px) {
  .sidebar {
    width: 208px;
  }

  .audios-player-dock {
    left: calc(208px + (100vw - 208px) / 2);
    width: min(calc(100vw - 244px), 680px);
  }
}

@media (max-width: 768px),
  (min-width: 769px) and (max-width: 1024px) and (orientation: portrait),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .audios-page {
    overflow-y: auto;
  }

  .content-wrapper {
    display: block;
    min-height: 100vh;
    height: auto;
    overflow: visible;
  }

  .sidebar {
    display: none;
  }

  .audios-shell {
    width: 100%;
    height: auto;
    display: block;
    overflow: visible;
    padding: 18px 8px 40px;
  }

  .audios-shell.has-player {
    padding-bottom: 158px;
  }

  .audio-list {
    overflow: visible;
    padding-right: 0;
    gap: 8px;
  }

  .audio-list.all-list-frame {
    padding: 12px 8px;
    border-radius: 28px;
  }

  .audio-list.saved-list-frame .audio-item {
    border-radius: 14px;
  }

  .audio-list.saved-list-frame {
    border-radius: 18px;
  }

  .audio-tabs {
    gap: 10px;
    margin-bottom: 18px;
  }

  .audio-tab {
    min-width: 90px;
    min-height: 42px;
    padding: 0 18px;
    font-size: 14px;
  }

  .mobile-topbar {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    min-height: 56px;
    padding: calc(env(safe-area-inset-top, 0px) + 6px) 16px 6px;
    z-index: 20;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .mobile-topbar.is-scrolled {
    background: rgba(249, 247, 242, 0.96);
    box-shadow: 0 8px 20px rgba(45, 52, 54, 0.08);
  }

  .mobile-topbar-offset {
    display: block;
    height: calc(env(safe-area-inset-top, 0px) + 56px);
  }

  .mobile-back-btn {
    display: inline-flex;
    position: absolute;
    left: 16px;
    top: calc(env(safe-area-inset-top, 0px) + 6px);
    width: 44px;
    height: 44px;
    border: 1px solid rgba(255, 255, 255, 0.85);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.82);
    color: #5f5044;
    font-size: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
    cursor: pointer;
    align-items: center;
    justify-content: center;
  }

  .mobile-topbar-title {
    flex: 1;
    min-width: 0;
    margin: 0;
    padding: 0 48px;
    color: #2d3436;
    font-size: 20px;
    font-weight: 800;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .audio-item {
    gap: 14px;
    min-height: 144px;
    padding: 14px 12px;
    border-radius: 14px;
  }

  .audio-cover-frame,
  .audio-skeleton-cover {
    width: 146px;
    min-height: 0;
    aspect-ratio: 16 / 9;
    height: auto;
    flex-basis: 146px;
    border-radius: 8px;
  }

  .audio-title {
    font-size: 19px;
  }

  .audio-subtitle {
    margin-top: 8px;
    font-size: 16px;
  }

  .audio-meta {
    gap: 12px;
    min-height: 30px;
    margin-top: 14px;
  }

  .audio-age-badge {
    min-width: 48px;
    height: 28px;
    padding: 0 9px;
    border-radius: 8px;
    font-size: 16px;
  }

  .audio-kind {
    gap: 5px;
    font-size: 15px;
  }

  .audio-kind svg {
    font-size: 15px;
  }

  .audio-list.saved-list-frame {
    gap: 12px;
  }

  .audio-list.saved-list-frame .audio-item {
    gap: 14px;
    min-height: 78px;
    padding: 14px 12px;
    border-radius: 18px;
  }

  .saved-audio-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    font-size: 21px;
  }

  .saved-audio-title {
    font-size: 16px;
  }

  .saved-favorite-btn {
    min-width: 78px;
    min-height: 36px;
    padding: 0 12px;
    gap: 6px;
    font-size: 12px;
  }

  .audios-player-dock {
    left: 50%;
    width: calc(100vw - 16px);
    bottom: 10px;
    padding: 12px;
    border-radius: 18px;
  }

  .audios-player-title {
    font-size: 14px;
  }

  .audio-skeleton-row {
    gap: 14px;
  }

  .saved-audio-skeleton-row {
    gap: 14px;
  }

  .saved-audio-skeleton-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
  }

  .saved-audio-skeleton-title {
    width: 72%;
    height: 16px;
  }

  .saved-audio-skeleton-date {
    width: 112px;
    height: 12px;
    margin-top: 9px;
  }

  .saved-audio-skeleton-button {
    width: 78px;
    height: 36px;
  }

  .audio-skeleton-title {
    width: 70%;
    height: 20px;
  }

  .audio-skeleton-subtitle {
    width: 86%;
    height: 17px;
    margin-top: 12px;
  }

  .audio-skeleton-meta {
    gap: 12px;
    margin-top: 16px;
  }

  .audio-skeleton-badge {
    width: 52px;
    height: 30px;
    border-radius: 8px;
  }

  .audio-skeleton-kind {
    width: 64px;
    height: 16px;
  }
}

@media (max-width: 768px),
  (min-width: 769px) and (max-width: 1024px) and (orientation: portrait),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .mobile-back-btn,
  .audios-player-dock {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  .nav-item,
  .audio-item,
  .saved-favorite-btn,
  .mobile-back-btn,
  .audios-player-dock {
    transition-property: background-color, color, border-color, box-shadow, transform, opacity;
  }
}
</style>
